import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';

import { Dropdown } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  SiteConfigContext,
  useDebouncedDispatchSiteConfig,
  useCountryContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  getDataSourceByKey,
  getCallingCodeDisplayByCountry,
  parseInitialPhoneNumber,
  composePhoneNumber,
  DATA_SOURCE_KEY_MAPPING,
  validatePhone,
  preventEventPropagation,
} from '@bingads-webui-campaign-react/smart-component-util';

import { SmartTextEditor } from './smart-text-editor';

const getStyles = () => ({
  phoneNumberEditorContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    marginBottom: '23px',

    '& .ms-TextField': {
      '& .ms-Toggle': {
        padding: '5px 0',
      },
      '& label.ms-Label span': {
        display: 'none',
      },
    },

    '& .smart-action-button-phone-input': {
      '& .ms-TextField-wrapper label.ms-Label': {
        display: 'none',
      },
    },

    '& .ms-TextField-errorMessage': {
      position: 'absolute',
      bottom: '-23px',
    },

    '& .ms-Toggle-innerContainer': {
      position: 'absolute',
      right: 0,
    },
  },
  phoneNumberCountryMenuContainer: {
    width: '30%',
  },
  phoneNumberEditorLabel: {
    marginBottom: '8px',
  },
});

const textEditorStyles = {
  root: {
    width: '65%',
    marginTop: 'auto',
  },
};


const onRenderOption = option => (
  <span id={option.key} className="country-option">{option.text}</span>
);

const onRenderTitle = ([option]) => (
  <span id={option.key} className="selected-country-option">{option.text}</span>
);

const getInitialCountry = (countries, id) => _.findWhere(
  countries,
  { Id: id }
) || _.first(countries);

const UnstyledSmartPhoneNumberEditor = (props) => {
  const {
    i18n,
    // Below two fields are exclusive(dataSourceName takes precedence)
    dataSourceName,
    value,
    initialCountryCode,
    onPhoneNumberChange,
    disabled,
    classes,
    label: labelProp,
    // Validations can be done inside or use passed in errorMessage
    // When use errorMessage, remember to pass ignoreInternalValidate as well
    errorMessage,
    ignoreInternalValidate,
    onRenderLabel,
    ...passThroughProps
  } = props;
  const { siteConfig } = useContext(SiteConfigContext);
  const debouncedDispatchSiteConfig = useDebouncedDispatchSiteConfig();

  const countryList = useCountryContext();
  const countries = _.pluck(countryList, 'value');

  const label = labelProp || i18n.getString(_TL_('Phone number'));
  // Inside the getDataSourceByKey: If dataSourceName is falsy, value would be used.
  const fullPhoneNumber = getDataSourceByKey(siteConfig, dataSourceName, value, false);
  const locationInfo = getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.LocationInfo, '', false);

  const {
    initialPhoneNumber,
    initialCallingCode,
    initialCountryId,
  } = parseInitialPhoneNumber(fullPhoneNumber, initialCountryCode, countries, locationInfo);

  const [selectedCountry, setSelectedCountry] = useState(getInitialCountry(
    countries,
    initialCountryId
  ));
  const [countryId, setCountryId] = useState(initialCountryId);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [callingCode, setCallingCode] = useState(initialCallingCode);
  const [internalErrorMessage, setInternalErrorMessage] = useState('');

  const onPhoneNumberInputChange = useCallback((newValue) => {
    if (newValue) {
      setPhoneNumber(newValue.value);
    }
  }, []);

  const onSelectOption = useCallback((e, option) => {
    preventEventPropagation(e);
    const selectedCountryId = Number(option.key);
    const countryConfig = _.findWhere(countries, { Id: selectedCountryId });
    const selectedCallingCode = countryConfig
      ? countryConfig.CountryCallingCode
      : initialCallingCode;

    setCountryId(selectedCountryId);
    setCallingCode(selectedCallingCode);
    setSelectedCountry(countryConfig);
  }, [countries, initialCallingCode]);

  // Validate, set siteConfig
  useEffect(() => {
    const phoneNumberWithCallingCode = composePhoneNumber(callingCode, phoneNumber);

    if (!ignoreInternalValidate) {
      const errMessage = validatePhone(phoneNumberWithCallingCode)
        ? ''
        : i18n.getString(_TL_('Invalid phone number'));
      setInternalErrorMessage(errMessage);
    }

    if (dataSourceName) {
      debouncedDispatchSiteConfig({
        type: 'UPDATE_DATASOURCE_BY_NAME',
        payload: {
          name: dataSourceName,
          value: phoneNumberWithCallingCode,
        },
      });
    }

    onPhoneNumberChange({
      countryId,
      callingCode,
      phoneNumber,
      phoneNumberWithCallingCode,
    });
  }, [
    countryId, callingCode, phoneNumber, dataSourceName, ignoreInternalValidate,
    onPhoneNumberChange, debouncedDispatchSiteConfig, i18n,
  ]);

  const options = _.map(countries, country => ({
    key: country.Id,
    text: getCallingCodeDisplayByCountry(country),
  }));

  const countryDropDown = useMemo(() => (
    <Dropdown
      className="phone-number-country-code-menu"
      selectedKey={selectedCountry ? selectedCountry.Id : countryId}
      label={label}
      options={options}
      onChange={onSelectOption}
      dropdownWidth="auto"
      disabled={disabled}
      onRenderTitle={onRenderTitle}
      onRenderOption={onRenderOption}
      onRenderLabel={onRenderLabel}
    />
  ), [selectedCountry, countryId, label, options, onSelectOption, disabled, onRenderLabel]);

  return (
    <div className={classNames(classes.phoneNumberEditorContainer, 'phone-number-editor-container')}>
      <div className={classNames(
        classes.phoneNumberCountryMenuContainer,
        'phone-number-country-menu-container'
      )}
      >
        {countryDropDown}
      </div>
      <SmartTextEditor
        value={phoneNumber}
        onChange={onPhoneNumberInputChange}
        customStyles={textEditorStyles}
        disabled={disabled}
        errorMessage={errorMessage || internalErrorMessage}
        {...passThroughProps}
      />
    </div>
  );
};

UnstyledSmartPhoneNumberEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  onPhoneNumberChange: PropTypes.func,
  onRenderLabel: PropTypes.func,
  initialCountryCode: PropTypes.string,
  label: PropTypes.string,
  dataSourceName: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  ignoreInternalValidate: PropTypes.bool,
};

UnstyledSmartPhoneNumberEditor.defaultProps = {
  onPhoneNumberChange: _.noop,
  // This is used in differentiating US and CA
  // TODO: Use US for now. Need to store the countryCode in dataSource in next PR
  initialCountryCode: 'US',
  label: '',
  dataSourceName: null,
  value: '',
  disabled: false,
  errorMessage: '',
  ignoreInternalValidate: false,
  onRenderLabel: undefined,
};

UnstyledSmartPhoneNumberEditor.displayName = 'SmartPhoneNumberEditor';

export const SmartPhoneNumberEditor = withDefaultStyles(
  UnstyledSmartPhoneNumberEditor,
  getStyles
);
